import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { IEnvironment } from '../../models/environment.model';
import { IApplication } from '../../models/application.model';
import {
  IApplicationDetails,
  IOfficerAttachment,
} from '../../models/application-details.model';
import { IHttpBaseResponse } from '../../../utils/models/http-base-response.model';
import { IHttpPagedResponse } from '../../../utils/models/http-paged-response.model';
import { IResponse } from '../../models/response.model';
import {
  IFile,
  IRequestAccessResponse,
} from '../../models/track-application.model';
import { ICertificateDownload } from '../../models/certificate-download.model';
import { ApplicationCertificateValidity } from '../../models/application-certificate-response-model';
import { ICertificate } from '../../models/certificates.model';
import { IApplicationResponse } from '../../models/application-response.model';
import { convertObjectToQueryString } from '../../../utils/utils/query-param-converter';
import { IBusinessApplication } from '../../models/business.model';
import { apis } from '../../../../app-apis';

interface IHttpPagedRecordResponse<T> extends IHttpBaseResponse {
  data: PagedRecord<T>;
}

export interface RfaAccessTokenResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  expiresAt: string;
}
export interface PagedRecord<T> {
  content: T;
  pageable: any;
  last: boolean;
  totalPages: number;
  totalElements: number;
  number: number;
  size: number;
  first: boolean;
  numberOfElements: number;
  empty: boolean;
  sort: any;
}
@Injectable({
  providedIn: 'root',
})
export class ApplicationsService {
  private baseUrl = '';
  private applicationAPIs = apis.application;

  private environment: IEnvironment;

  constructor(
    private http: HttpClient,
    @Inject('environment') environment: IEnvironment
  ) {
    this.environment = environment;
    this.baseUrl = environment.apiGatewayBaseUrl + '/application';
  }

  getApplications(queryParams: {
    page: number;
    size: number;
    sort: string;
    searchTerm: string;
  }) {
    const queryParamsStr = convertObjectToQueryString(queryParams);
    return this.http.get<IHttpPagedResponse<IApplication>>(
      `${this.baseUrl}${this.applicationAPIs.applications}?${queryParamsStr}`
    );
  }

  getBusinessApplications(
    businessId: string,
    queryParams: {
      page: number;
      size: number;
      sort: string;
      searchTerm: string;
    }
  ) {
    const queryParamsStr = convertObjectToQueryString(queryParams);
    return this.http.get<IHttpPagedResponse<IBusinessApplication>>(
      `${this.baseUrl}${this.applicationAPIs.businessApplications(
        businessId
      )}?${queryParamsStr}`
    );
  }

  getBusinessCertificates(
    businessId: string,
    queryParams: {
      page: number;
      size: number;
      sort: string;
      searchTerm: string;
    }
  ) {
    const queryParamsStr = convertObjectToQueryString(queryParams);
    return this.http.get<IHttpPagedRecordResponse<ICertificate[]>>(
      `${this.baseUrl}${this.applicationAPIs.businessCertificates(
        businessId
      )}?${queryParamsStr}`
    );
  }

  getBusinessOfficersAttachments(
    businessId: string,
    queryParams: {
      page: number;
      size: number;
      sort: string;
      searchTerm: string;
    }
  ) {
    const queryParamsStr = convertObjectToQueryString(queryParams);
    return this.http.get<IHttpPagedRecordResponse<IOfficerAttachment[]>>(
      `${this.baseUrl}${this.applicationAPIs.businessOfficerAttachments(
        businessId
      )}?${queryParamsStr}`
    );
  }

  getApplicationDetailsById(
    applicationId: string
  ): Observable<IApplicationDetails> {
    return this.http
      .get<IResponse<IApplicationDetails>>(
        `${this.baseUrl}${this.applicationAPIs.details}`,
        {
          headers: { id: applicationId },
        }
      )
      .pipe(map(res => res.data));
  }

  getApplicationByBillId(billId: string): Observable<IApplicationResponse> {
    return this.http
      .get<IResponse<IApplicationResponse>>(
        `${this.baseUrl}${this.applicationAPIs.billId}`,
        {
          headers: { billId },
        }
      )
      .pipe(map(res => res.data));
  }

  getApplicationRfaObject(id: string): Observable<any> {
    return this.http
      .get<IResponse<any>>(`${this.baseUrl}${this.applicationAPIs.rfaObject}`, {
        headers: { id },
      })
      .pipe(map(res => res.data));
  }

  getApplicationFormState(id: string): Observable<any> {
    return this.http
      .get<IResponse<any>>(`${this.baseUrl}${this.applicationAPIs.formState}`, {
        headers: { id },
      })
      .pipe(map(res => res.data));
  }

  getApplicationFeedbacks(id: string): Observable<any> {
    return this.http
      .get<IResponse<any>>(`${this.baseUrl}${this.applicationAPIs.feedbacks}`, {
        headers: { id },
      })
      .pipe(map(res => res.data));
  }

  getApplicationJourneyFeedbacks(id: string): Observable<any> {
    return this.http
      .get<IResponse<any>>(
        `${this.baseUrl}${this.applicationAPIs.journeyFeedbacks}`,
        {
          headers: { id },
        }
      )
      .pipe(map(res => res.data));
  }

  getActiveCertificates() {
    return this.http.get<IResponse<Record<string, ICertificate[]>>>(
      `${this.baseUrl}${this.applicationAPIs.userCertificates}`
    );
  }

  getAlmostExpiringCertificates() {
    return this.http.get<IResponse<ICertificate[]>>(
      `${this.baseUrl}${this.applicationAPIs.userExpiringCertificates}`
    );
  }

  getExpiredCertificates() {
    return this.http.get<IResponse<ICertificate[]>>(
      `${this.baseUrl}${this.applicationAPIs.userExpiredCertificates}`
    );
  }

  getTrackApplicatonByBillId(billId: string) {
    return this.http.get<IResponse<IApplication>>(
      `${this.baseUrl}${this.applicationAPIs.billIdPublic}`,
      {
        headers: { billId },
      }
    );
  }

  getTrackApplicatonByApplicationId(applicationNumber: string) {
    return this.http.get<IResponse<IApplication>>(
      `${this.baseUrl}${this.applicationAPIs.applicationNumberPublic}`,
      {
        headers: { applicationNumber },
      }
    );
  }

  checkApplicationCertificateValidity(applicationNumber: string) {
    return this.http.get<IResponse<ApplicationCertificateValidity>>(
      `${this.baseUrl}${this.applicationAPIs.applicationCertificateValidity}?applicationNumber=${applicationNumber}`
    );
  }

  verifyApplicationToken(applicationNumber: string, token: string) {
    const encodedToken = encodeURIComponent(btoa(token));
    return this.http.get<IResponse<ICertificateDownload>>(
      `${this.baseUrl}/v1/application/${applicationNumber}/certificates/preview?token=${encodedToken}`
    );
  }

  requestAccessByApplicationNumber(applicationNumber: string, locale: string) {
    return this.http.post<IResponse<IRequestAccessResponse>>(
      `${this.baseUrl}/v1/request-access/${applicationNumber}`,
      { locale }
    );
  }

  verifyRfaAccess(applicationNumber: string, otp: string) {
    return this.http.post<IResponse<RfaAccessTokenResponse>>(
      `${this.baseUrl}/v1/rfa-access/verify`,
      {
        applicationNumber,
        otp: btoa(otp),
      }
    );
  }

  getApplicationDetailsByOTP(applicationNumber: string, otp: string) {
    let url = `${this.baseUrl}/v1/application/${applicationNumber}/details-by-otp`;
    if (otp.length > 0) {
      const encodedToken = encodeURIComponent(btoa(otp));
      url = url + `?otp=${encodedToken}`;
    }
    return this.http.get<IResponse<Record<string, unknown>>>(url);
  }

  getPaymentReceiptByOTP(
    applicationNumber: string,
    otp: string,
    locale?: string
  ) {
    let url = `${this.baseUrl}/v1/payment-receipt-by-otp`;
    if (otp.length > 0) {
      const encodedToken = encodeURIComponent(btoa(otp));
      url = url + `?otp=${encodedToken}`;
    }
    // Add the locale header if provided
    const headers: Record<string, string> = { applicationNumber };
    if (locale) {
      headers['locale'] = locale;
    }
    return this.http.get<IResponse<IFile>>(url, {
      headers: headers,
    });
  }

  getCertificateByOTP(applicationNumber: string, otp: string, locale: string) {
    let url = `${this.baseUrl}/v1/application/${applicationNumber}/certificate-by-otp?locale=${locale}`;
    if (otp.length > 0) {
      const encodedToken = encodeURIComponent(btoa(otp));
      url = url + `&otp=${encodedToken}`;
    }
    return this.http.get<IResponse<IFile>>(url);
  }

  downloadGov2Certificate(applicationNumber: string, language: string) {
    return this.http.get<IResponse<any>>(
      `${this.baseUrl}/v1/gov2-compat/download-gov2-certificate`,
      {
        params: { applicationNumber, language },
      }
    );
  }

  downloadGov2Receipt(applicationNumber: string, language: string) {
    return this.http.get<IResponse<any>>(
      `${this.baseUrl}/v1/gov2-compat/download-gov2-receipt`,
      {
        params: { applicationNumber, language },
      }
    );
  }

  getOfficersAttachments() {
    return this.http.get<IResponse<Record<string, IOfficerAttachment[]>>>(
      `${this.baseUrl}${this.applicationAPIs.userOfficersAttachments}`
    );
  }

  downloadAttachment(applicationId: string, fileName: string) {
    return this.http.get<Blob>(
      `${this.baseUrl}${this.applicationAPIs.attachment}`,
      {
        params: { id: applicationId, fileName },
        responseType: 'blob' as 'json',
        observe: 'response',
      }
    );
  }

  downloadAttachmentByOTP(
    applicationNumber: string | undefined,
    otp: string,
    fileName: string
  ) {
    let url = `${this.baseUrl}/v1/file-manager/application/${applicationNumber}/attachment-by-otp?fileName=${fileName}`;
    if (otp.length > 0) {
      const encodedToken = encodeURIComponent(btoa(otp));
      url = url + `&otp=${encodedToken}`;
    }
    return this.http.get<Blob>(url, {
      responseType: 'blob' as 'json',
      observe: 'response',
    });
  }

  verifyOTP(applicationNumber: string, otp: string) {
    const encodedToken = encodeURIComponent(btoa(otp));
    return this.http.get<IResponse<boolean>>(
      `${this.baseUrl}/v1/verify-otp/${applicationNumber}?otp=${encodedToken}`
    );
  }

  getPaymentPublicKey(merchant: string): Observable<IResponse<any>> {
    return this.http.get<IResponse<any>>(
      `${this.environment.apiGatewayBaseUrl}/application/v1/payment-merchant/public-key`,
      {
        headers: { merchant },
      }
    );
  }

  /**
   * Get payment merchant public key for legacy (Gov 2.0) applications.
   * @param merchant - The merchant code (e.g., GOR, RDB)
   * @param locale - The user's locale
   * @returns Observable with payment merchant key
   */
  getLegacyPaymentMerchantKey(
    merchant: string,
    locale: string
  ): Observable<IResponse<{ merchant: string; publicKey: string }>> {
    return this.http.get<IResponse<{ merchant: string; publicKey: string }>>(
      `${this.baseUrl}${this.applicationAPIs.federatedPaymentMerchantKey}`,
      {
        headers: { merchant, locale },
      }
    );
  }
}
